import { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";

// Deliberate duplicate of `outbound/.../tasks/-data/wire.ts`'s `zoneLabelsOf` /
// `customerNamesOf` (and their types). The internal and outbound route trees
// are kept uncoupled and must not import from each other, so this is copied,
// not shared. If one changes, check the other.

type Schemas = TaskPlannerSchemaTypes.components["schemas"];

/** One snapshotted zone, as both listings state it. */
export type WireTaskPlanZone = Schemas["TaskPlanZone"];

/** One snapshotted customer, as both listings state it. */
export type WireTaskPlanCustomer = Schemas["TaskPlanCustomerSnapshot"];

/**
 * A plan's or task's zones as a label list, de-duplicated by `zone_ext_id` -
 * which is the whole reason this is a function and not a `.map`: `zones` is one
 * entry per *item snapshot* (`TaskPlanZone.item_snapshot_id`), so a plan with
 * forty picks in Dry Grocery states Dry Grocery forty times.
 *
 * The **abbreviation**, uppercased: each zone is configured with a three-letter
 * code and that is what a tag should say, so a Freezer task reads FRZ.
 * `zone_abbreviation` is optional on the wire and genuinely absent in practice,
 * so an absent one falls back to the uppercased `zone_name`. Uppercasing the
 * fallback too keeps the column one shape rather than mixing "FRZ" with
 * "Packout".
 */
export function zoneLabelsOf(zones: ReadonlyArray<WireTaskPlanZone>): string[] {
  const byExtId = new Map<number, string>();
  zones.forEach((zone) =>
    byExtId.set(
      zone.zone_ext_id,
      (zone.zone_abbreviation ?? zone.zone_name).toUpperCase(),
    ),
  );
  return [...byExtId.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * A plan's or task's customers as a name list, de-duplicated by
 * `customer_ext_id` - the same reason `zoneLabelsOf` is a function and not a
 * `.map`: `customers` is one entry per *snapshot row* (one per stop,
 * `TaskPlanCustomerSnapshot.location_id`), so a customer with two stops on the
 * plan is stated twice, each with its own `location_id`.
 *
 * The full `name`, not an abbreviation: unlike a zone there is no short code
 * on the wire. Sorted by name rather than left in the wire's
 * `customer_ext_id` order, so the cell reads alphabetically.
 *
 * `customers` is optional on every row shape (the server always sends it, as
 * an empty array, but the property is not `required`), so a call site passes
 * `row.customers ?? []` rather than this helper accepting `undefined`.
 */
export function customerNamesOf(
  customers: ReadonlyArray<WireTaskPlanCustomer>,
): string[] {
  const byExtId = new Map<number, string>();
  customers.forEach((customer) =>
    byExtId.set(customer.customer_ext_id, customer.name),
  );
  return [...byExtId.values()].sort((a, b) => a.localeCompare(b));
}
